import { compilePossessorStatusChain } from './possessor-compile';
import type { PossessorCompileContext, PossessorCompileResult, PossessorStatusMutation } from './possessor-compile';
import { freezeFromBaseRow } from './from-base-context';
import { yieldBetweenMemberCompiles } from './compile-task';
import { encounterLightTypesPresent } from '../../encounter-map/ft3d/light-presence';
import { ecLean } from './ec-lean-reader';

type Row = Record<string, unknown>;
type Scene = ReturnType<NonNullable<PossessorCompileContext['conditionScene']>>;
export interface LongRestWorkerInput {
    row: Row; chain: PossessorStatusMutation[];
    context: Pick<PossessorCompileContext, 'createdById' | 'catalog' | 'catalogDefinitionId' | 'floorRows' | 'contract'>;
    definitions: Record<string, Row | null>; occurrences: Record<string, Row | null>;
    roster?: unknown[]; scene?: Scene; light?: string[] | null; lightAfter: boolean;
    charges?: Record<string, number | null | undefined>;
}
export interface LongRestWorkerPatch {
    keys: string[];
    definitions: LongRestWorkerInput['definitions']; occurrences: LongRestWorkerInput['occurrences'];
    dynamic: Omit<LongRestWorkerInput, 'row' | 'chain' | 'context' | 'definitions' | 'occurrences'>;
}
/** Only a C1 carrier's owned row/chain/catalog stay fixed during its missing-definition retries.
 * Scene/roster/charge snapshots and all changed definitions are always refreshed. */
export function longRestWorkerPatch(previous: LongRestWorkerInput, input: LongRestWorkerInput): LongRestWorkerPatch {
    const { row: _row, chain: _chain, context: _context, definitions, occurrences, ...dynamic } = input;
    const changed = (before: Record<string, Row | null>, after: Record<string, Row | null>) =>
        Object.fromEntries(Object.entries(after).filter(([id, value]) => before[id] !== value));
    return { keys: Object.keys(definitions), definitions: changed(previous.definitions, definitions),
        occurrences: changed(previous.occurrences, occurrences), dynamic };
}
export function applyLongRestWorkerPatch(previous: LongRestWorkerInput, patch: LongRestWorkerPatch): LongRestWorkerInput {
    const merge = (before: Record<string, Row | null>, changes: Record<string, Row | null>) => Object.fromEntries(
        patch.keys.map(id => [id, Object.prototype.hasOwnProperty.call(changes, id) ? changes[id]! : before[id]! ]));
    return { row: previous.row, chain: previous.chain, context: previous.context,
        definitions: merge(previous.definitions, patch.definitions), occurrences: merge(previous.occurrences, patch.occurrences), ...patch.dynamic };
}
const uuid = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i;
const row = (value: unknown): value is Row => value !== null && typeof value === 'object' && !Array.isArray(value);
export interface LongRestStaticScan { objects: WeakSet<object>; ids?: string[] }

/** Snapshot the synchronous context readers before crossing the worker boundary.
 * Scan the already-held closure; absent definitions remain absent and use the
 * compiler's existing named missing-definition retry, with no new fetch here. */
export async function longRestWorkerInput(carrier: Row, chain: PossessorStatusMutation[], ctx: PossessorCompileContext,
    staticScan: LongRestStaticScan = { objects: new WeakSet() }): Promise<LongRestWorkerInput> {
    if (ctx.admitStatusLayer) throw new Error('long-rest-worker-test-layer');
    const input: LongRestWorkerInput = { row: carrier, chain,
        context: { createdById: ctx.createdById, catalog: ctx.catalog, catalogDefinitionId: ctx.catalogDefinitionId,
            floorRows: ctx.floorRows, contract: ctx.contract },
        definitions: {}, occurrences: {}, lightAfter: !!ctx.lightTypesPresentAfter,
        ...(ctx.roster ? { roster: ctx.roster() } : {}),
        ...(ctx.conditionScene ? { scene: ctx.conditionScene() } : {}),
        ...(ctx.lightTypesPresent ? { light: ctx.lightTypesPresent() } : {}),
        ...(ctx.charges ? { charges: {} } : {}) };
    const ids = new Set<string>(), stack: unknown[] = [];
    const holder = String(ecLean(carrier, 'encounter_creature_id') ?? '').toUpperCase();
    let visited = 0;
    const definition = (value: string): void => {
        if (value.length !== 36 || ids.has(value) || !uuid.test(value)) return;
        const id = value.toUpperCase();
        if (ids.has(id)) return;
        ids.add(id);
        input.definitions[id] = ctx.definition(id);
        input.occurrences[id] = ctx.occurrenceDefinition?.(id) ?? input.definitions[id]!;
        if (ctx.charges) input.charges![id] = ctx.charges(holder, id);
        stack.push(input.definitions[id], input.occurrences[id]);
    };
    async function scan(roots: unknown[], immutable: boolean): Promise<void> {
        const objects = immutable ? staticScan.objects : new WeakSet<object>();
        stack.push(...roots);
        while (stack.length) {
            const value = stack.pop();
            if (value !== null && typeof value === 'object' && !objects.has(value)
                && (immutable || !staticScan.objects.has(value))) {
                objects.add(value);
                for (const key of Object.keys(value)) {
                    definition(key);
                    const child = (value as Row)[key];
                    if (typeof child === 'string') definition(child);
                    else if (child !== null && typeof child === 'object') stack.push(child);
                    if (++visited % 1024 === 0) await yieldBetweenMemberCompiles();
                }
            }
        }
    }
    // Only the exact private carrier/catalog snapshot reuses this scan. Re-read
    // every definition; new replacement objects extend its transitive closure.
    if (staticScan.ids) for (const id of staticScan.ids) {
        definition(id);
        if (++visited % 1024 === 0) await yieldBetweenMemberCompiles();
    }
    await scan(staticScan.ids ? [] : [carrier, chain, input.context], true);
    staticScan.ids = [...ids];
    await scan([input.roster, input.scene, input.light, input.charges], false);
    return input;
}

/** The same status-chain compiler, with immutable definitions and data-only
 * scene readers. No page caches or browser state enter this worker context. */
export function compileLongRestWorker(input: LongRestWorkerInput): PossessorCompileResult {
    const holder = String(ecLean(input.row, 'encounter_creature_id') ?? '').toUpperCase();
    const ctx: PossessorCompileContext = { ...input.context,
        definition: id => input.definitions[id.toUpperCase()] ?? null,
        occurrenceDefinition: id => input.occurrences[id.toUpperCase()] ?? null,
        ...(input.roster ? { roster: () => input.roster! } : {}),
        ...(input.scene !== undefined ? { conditionScene: () => input.scene! } : {}),
        ...('light' in input ? { lightTypesPresent: () => input.light ?? null } : {}),
        ...(input.charges ? { charges: (owner: string, id: string) => owner.toUpperCase() === holder ? input.charges![id.toUpperCase()] : undefined } : {}),
    };
    for (const definitions of [input.definitions, input.occurrences]) for (const definition of Object.values(definitions))
        if (definition) freezeFromBaseRow(definition);
    if (input.context.catalog) freezeFromBaseRow(input.context.catalog);
    if (input.lightAfter) ctx.lightTypesPresentAfter = patch => {
        if (!input.scene?.encounter) return undefined;
        const encounter = input.scene!.encounter!, roster = encounter.encounter_creatures_arr;
        const members = Array.isArray(roster) ? roster : row(roster) ? Object.values(roster) : [];
        let found = false;
        const patched = members.map(member => {
            if (!row(member) || String(ecLean(member, 'encounter_creature_id') ?? '').toUpperCase() !== holder) return member;
            found = true; return patch(member);
        });
        return found ? encounterLightTypesPresent({ ...encounter, encounter_creatures_arr: patched }, input.scene!.campaign, ecLean(input.row, 'encounter_light')) : undefined;
    };
    return compilePossessorStatusChain(input.row, input.chain, ctx, { derivedOnly: true });
}
